import DurationUnits from '@/constants/date/DurationUnits'
import { checkCacheFreshness } from '@/helpers/cache-helpers'
import { fail, success } from '@/helpers/result-helper'
import { VuexResponse } from '@/helpers/vuex-action-builder'
// eslint-disable-next-line no-unused-vars
import ResultDTO from '@/models/app/resultDTO'
import { TimeEntryBookingsErrorModel } from '@/models/booking/timeEntries/errors/timeEntryBookingsErrorModel'
import { TimeEntryClockInErrorModel } from '@/models/booking/timeEntries/errors/timeEntryClockInErrorModel'
import { TimeEntryClockOutErrorModel } from '@/models/booking/timeEntries/errors/timeEntryClockOutErrorModel'
import { TimeEntryMealBreakEndErrorModel } from '@/models/booking/timeEntries/errors/timeEntryMealBreakEndErrorModel'
import { TimeEntryMealBreakStartErrorModel } from '@/models/booking/timeEntries/errors/timeEntryMealBreakStartErrorModel'
import { TimeEntryMealBreakStatusErrorModel } from '@/models/booking/timeEntries/errors/timeEntryMealBreakStatusErrorModel'
import { TimeEntryStatusErrorModel } from '@/models/booking/timeEntries/errors/timeEntryStatusErrorModel'
// eslint-disable-next-line no-unused-vars
import { MealBreakFormDataDTO } from '@/models/booking/timeEntries/mealBreakFormDataDTO'
// eslint-disable-next-line no-unused-vars
import { TimeEntryClockInRequest } from '@/models/booking/timeEntries/requests/timeEntryClockInRequest'
// eslint-disable-next-line no-unused-vars
import { TimeEntryClockOutRequest } from '@/models/booking/timeEntries/requests/timeEntryClockOutRequest'
// eslint-disable-next-line no-unused-vars
import { TimeEntryMealBreakStartRequest } from '@/models/booking/timeEntries/requests/timeEntryMealBreakStartRequest'
import { MealBreakStatusResponse } from '@/models/booking/timeEntries/responses/mealBreakStatusResponse'
import { TimeEntryBookingsResponse } from '@/models/booking/timeEntries/responses/timeEntryBookingsResponse'
import { TimeEntryClockOutResponse } from '@/models/booking/timeEntries/responses/timeEntryClockOutResponse'
import { TimeEntryStatusResponse } from '@/models/booking/timeEntries/responses/timeEntryStatusResponse'
import Duration from '@/models/general/duration'
import CompileEndMealBreakResponsePayload from '@/services/bookings/timeEntries/CompileEndMealBreakResponsePayload'
import dayjs from '@/services/date/index'

const getDefaultState = () => {
  return {
    status: null,
    statusLoadingCount: 0,
    mealBreakLoadingCount: 0,
    cicoLoadingCount: 0,
    shiftsLoadingCount: 0,
    mealBreakStatusLoadingCount: 0,
    clockOutStatusLoadingCount: 0,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    moduleName: () => 'time-entries',
    timeEntryStatus: (state) => state.status?.data || null,
    isLoadingStatus: (state) => state.statusLoadingCount > 0,
    isLoadingMealBreakOperation: (state) => state.mealBreakLoadingCount > 0,
    isLoadingCicoOperation: (state) => state.cicoLoadingCount > 0,
    isLoadingShifts: (state) => state.shiftsLoadingCount > 0,
    isLoadingMealBreakStatus: (state) => state.mealBreakStatusLoadingCount > 0,
    isLoadingClockOutStatus: (state) => state.clockOutStatusLoadingCount > 0,
  },
  mutations: {
    START_LOADING_STATUS(state) {
      state.statusLoadingCount++
    },
    FINISH_LOADING_STATUS(state) {
      state.statusLoadingCount--
    },
    START_LOADING_MEAL_BREAK_OP(state) {
      state.mealBreakLoadingCount++
    },
    FINISH_LOADING_MEAL_BREAK_OP(state) {
      state.mealBreakLoadingCount--
    },
    START_LOADING_CICO_OP(state) {
      state.cicoLoadingCount++
    },
    FINISH_LOADING_CICO_OP(state) {
      state.cicoLoadingCount--
    },
    START_LOADING_SHIFTS(state) {
      state.shiftsLoadingCount++
    },
    FINISH_LOADING_SHIFTS(state) {
      state.shiftsLoadingCount--
    },
    SET_STATUS(state, status) {
      state.status = {
        lastUpdated: dayjs(),
        data: new TimeEntryStatusResponse(status),
      }
    },
    START_LOADING_MEAL_BREAK_STATUS(state) {
      state.mealBreakStatusLoadingCount++
    },
    FINISH_LOADING_MEAL_BREAK_STATUS(state) {
      state.mealBreakStatusLoadingCount--
    },
    START_LOADING_CLOCK_OUT_STATUS(state) {
      state.clockOutStatusLoadingCount++
    },
    FINISH_LOADING_CLOCK_OUT_STATUS(state) {
      state.clockOutStatusLoadingCount--
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    /**
     * Retrieves the current clock in status for the candidate on the current day
     *
     * @param {{commit: Function, state: Object, dispatch: Function}} vuexContext
     * @param {Boolean} forceRefresh Skips the cached status data if requested
     * @returns {ResultDTO<TimeEntryStatusResponse>}
     */
    async retrieveStatus({ commit, state, dispatch }, forceRefresh = false) {
      // TODO: Refactor when implementing unscheduled clock-in/clock-out functionality
      const isCacheFresh = checkCacheFreshness(
        new Duration({ value: 1, unit: DurationUnits.HOUR }),
        state.status?.lastUpdated,
        forceRefresh
      )

      if (isCacheFresh) {
        return success({ data: state.status.data })
      }

      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.getStatus())
        .withLoading('START_LOADING_STATUS', 'FINISH_LOADING_STATUS')
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryStatusErrorModel(result) })

      commit('SET_STATUS', result.data)
      return success({ data: state.status.data })
    },
    /**
     * Retrieves all the bookings that the candidate can clock in to on the
     * current day
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @returns {ResultDTO<TimeEntryBookingsResponse>}
     */
    async retrieveTodaysShifts({ commit, dispatch }) {
      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.getTodaysShifts())
        .withLoading('START_LOADING_SHIFTS', 'FINISH_LOADING_SHIFTS')
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryBookingsErrorModel(result) })

      return success({ data: new TimeEntryBookingsResponse(result.data) })
    },
    /**
     * Clocks a candidate in to the specified booking
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @param {TimeEntryClockInRequest} payload
     * @returns {ResultDTO<TimeEntryStatusResponse>}
     */
    async clockIn({ commit, dispatch }, payload) {
      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.clockIn(payload))
        .withLoading('START_LOADING_CICO_OP', 'FINISH_LOADING_CICO_OP')
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryClockInErrorModel(result) })

      return success({ data: new TimeEntryStatusResponse(result.data) })
    },
    /**
     * Clocks a candidate out of a specified booking
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @param {TimeEntryClockOutRequest} payload
     * @returns {ResultDTO<TimeEntryClockOutResponse>}
     */
    async clockOut({ commit, dispatch }, payload) {
      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.clockOut(payload))
        .withLoading('START_LOADING_CICO_OP', 'FINISH_LOADING_CICO_OP')
        .withSuccessToast(
          this.$i18n.t('bookings.timeEntries.clockOut.api.successToast')
        )
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryClockOutErrorModel(result) })

      return success({ data: new TimeEntryClockOutResponse(result.data) })
    },
    /**
     * Starts a meal break on a specified booking
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @param {TimeEntryMealBreakStartRequest} payload
     * @returns {ResultDTO<TimeEntryStatusResponse>}
     */
    async startMealBreak({ commit, dispatch }, payload) {
      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.startMealBreak(payload))
        .withLoading(
          'START_LOADING_MEAL_BREAK_OP',
          'FINISH_LOADING_MEAL_BREAK_OP'
        )
        .withSuccessToast(
          this.$i18n.t('bookings.timeEntries.mealBreak.start.api.successToast')
        )
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryMealBreakStartErrorModel(result) })

      return success({ data: new TimeEntryStatusResponse(result.data) })
    },
    /**
     * Ends a meal break on a specified booking
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @param {{bookingId: number, formData: MealBreakFormDataDTO | null}} data
     * @returns {ResultDTO<TimeEntryStatusResponse>}
     */
    async endMealBreak({ commit, dispatch }, data) {
      const payload = CompileEndMealBreakResponsePayload(data)

      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.endMealBreak(payload))
        .withLoading(
          'START_LOADING_MEAL_BREAK_OP',
          'FINISH_LOADING_MEAL_BREAK_OP'
        )
        .withSuccessToast(
          this.$i18n.t('bookings.timeEntries.mealBreak.end.api.successToast')
        )
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryMealBreakEndErrorModel(result) })

      return success({ data: new TimeEntryStatusResponse(result.data) })
    },
    /**
     * Updates the time entry status current stored in app state
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @param {TimeEntryStatusResponse} status
     */
    updateTimeEntryStatus({ commit }, status) {
      commit('SET_STATUS', status)
    },
    /**
     * Retrieves the status of the meal break for the booking the candidate
     * has clocked into.
     *
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @returns {ResultDTO<MealBreakStatusResponse>}
     */
    async getMealBreakStatus({ commit, dispatch }) {
      const result = await new VuexResponse(commit, dispatch)
        .request(() => this.$api.timeEntries.getMealBreakStatus())
        .withLoading(
          'START_LOADING_MEAL_BREAK_STATUS',
          'FINISH_LOADING_MEAL_BREAK_STATUS'
        )
        .withStatusCode()
        .go()

      if (!result?.isSuccess)
        return fail({ error: new TimeEntryMealBreakStatusErrorModel(result) })

      return success({ data: new MealBreakStatusResponse(result.data) })
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
